/**
 * utils/leadStageFlow.ts — UNA sola definición de «¿puede este prospecto pasar
 * de esta etapa a esta otra?».
 *
 * El embudo es de un solo sentido y termina en dos puertas que no giran:
 *
 *     Nuevo → Contactado → Visita → Estimado → Propuesta ─┬─→ GANADO
 *                                                         └─→ Perdido
 *
 * Entre las etapas ACTIVAS el prospecto se mueve libre en los dos sentidos: la
 * realidad de una obra no es una fila india y devolver un prospecto de «Visita»
 * a «Contactado» es corregir un dato, no romper un proceso.
 *
 * Lo que sí es en firme son las dos salidas, y no por el mismo motivo:
 *
 * GANADO es irreversible A NIVEL DE DATOS. Ganar un prospecto CREA la ficha de
 * cliente y le traspasa las propuestas y el proyecto que hubiera generado
 * (`convertLeadToClient`). Sacarlo de Ganado dejaría un cliente sin prospecto,
 * unas propuestas apuntando a un trato que ya no existe, y ninguna pantalla en
 * la app para arreglarlo. Por eso se blinda en la capa de ESCRITURA
 * (`isLeadStageLocked`): ninguna ruta puede sacar de ahí un prospecto, ni las
 * que dispara el sistema solo.
 *
 * PERDIDO → GANADO es una regla de PROCESO, no de datos: perder un prospecto no
 * crea ni destruye nada, solo dice que el trato se cayó. Se bloquea en todos los
 * caminos que abre una persona —el selector de etapa, arrastrar la tarjeta, el
 * botón de convertir— para que un clic despistado no dé por ganado algo que se
 * archivó como perdido. Un prospecto perdido SÍ puede volver a una etapa activa:
 * el cliente que vuelve a llamar dos meses después es lo más normal del mundo, y
 * sin esa vuelta el usuario se quedaría encerrado —crear un prospecto nuevo se
 * lo bloquea el filtro de duplicados, que ve el mismo teléfono—.
 *
 * La única excepción a lo anterior es la firma del cliente en el enlace público:
 * ahí el trato está cerrado de verdad, y bloquearla dejaría una propuesta
 * firmada sin ficha de cliente. Ese camino llama a `convertLeadToClient` sin
 * pasar por `canChangeLeadStage`; ver el barrido de `useLeadProposalConversion`.
 */
import { LeadStatus } from '../types';
import type { Traducir } from '../i18n/idioma';

/** Las etapas por las que un prospecto se mueve mientras el trato sigue vivo. */
export const ACTIVE_STAGES: LeadStatus[] = [
    LeadStatus.NEW,
    LeadStatus.CONTACTED,
    LeadStatus.SITE_VISIT,
    LeadStatus.ESTIMATE_NEEDED,
    LeadStatus.PROPOSAL_SENT,
];

export const isActiveStage = (s: LeadStatus): boolean => ACTIVE_STAGES.includes(s);

/**
 * ¿Esta etapa es una puerta cerrada a nivel de datos?
 *
 * Solo GANADO. Es la invariante que se comprueba en la mutación, así que tiene
 * que ser exactamente lo que NINGUNA ruta puede deshacer. Perdido no entra: se
 * puede reactivar, y su restricción (no saltar a Ganado) es de proceso.
 */
export const isLeadStageLocked = (s: LeadStatus): boolean => s === LeadStatus.WON;

/**
 * ¿Se puede quitar del tablero sin perder nada?
 *
 * Solo un GANADO. No es una excepción a que su etapa esté cerrada: quitarlo no
 * lo devuelve al embudo ni deshace nada. Ganarlo YA creó su ficha de cliente y
 * le traspasó las propuestas y el proyecto, así que la fila del prospecto es
 * una copia: el contacto, el trato y el trabajo siguen todos en Clientes.
 *
 * Un prospecto activo NO: borrarlo pierde la única constancia de esa
 * conversación. Y uno perdido tampoco: es lo que impide volver a apuntarlo por
 * error —el filtro de duplicados lo reconoce por el teléfono— y lo que deja
 * revivirlo si el cliente vuelve a llamar.
 */
export const isLeadRemovableFromBoard = (s: LeadStatus): boolean => s === LeadStatus.WON;

export type StageBlockReason = 'won-is-final' | 'lost-cannot-jump-to-won';

/**
 * Qué impide este cambio de etapa, o `null` si nada lo impide.
 *
 * Devuelve el motivo en vez de un booleano —mismo patrón que
 * `findDuplicateContact`— porque quien bloquea tiene que poder explicar por qué,
 * y porque este proyecto compila sin `strict`: ahí TypeScript no estrecha una
 * unión por un discriminante booleano, así que un `{ allowed, reason }` obligaría
 * a un aserto en cada llamada.
 *
 * Quedarse donde está SIEMPRE vale. No es un detalle: `convertLeadToClient` es
 * idempotente y vuelve a escribir `status: Won` sobre un prospecto que ya está
 * en Ganado cada vez que se reintenta. Tratar eso como un movimiento prohibido
 * rompería el reintento, que es justo lo que garantiza que un cliente no se
 * duplique.
 */
export const findStageBlock = (from: LeadStatus, to: LeadStatus): StageBlockReason | null => {
    if (from === to) return null;
    if (from === LeadStatus.WON) return 'won-is-final';
    if (from === LeadStatus.LOST && to === LeadStatus.WON) return 'lost-cannot-jump-to-won';
    return null;
};

/**
 * Las etapas que se le pueden ofrecer a este prospecto, en el orden del embudo.
 *
 * Lo que no se puede hacer no se enseña: el selector y el tablero se dibujan
 * desde aquí, así que el bloqueo de arriba es la última red y no la primera
 * noticia. Incluye siempre la etapa actual — un selector que no contiene su
 * propio valor se pinta vacío.
 */
export const allowedStagesFrom = (from: LeadStatus): LeadStatus[] => {
    if (from === LeadStatus.WON) return [LeadStatus.WON];
    if (from === LeadStatus.LOST) return [...ACTIVE_STAGES, LeadStatus.LOST];
    return [...ACTIVE_STAGES, LeadStatus.WON, LeadStatus.LOST];
};

// ── El error, marcado por código y no por texto ────────────────
// Mismo criterio que el bloqueo por duplicado: el mensaje va traducido, así que
// reconocerlo por su redacción deja de funcionar en cuanto cambia el idioma.

export const STAGE_LOCKED_CODE = 'LEAD_STAGE_LOCKED';

export const stageLockedError = (message: string): Error => {
    const err = new Error(message);
    (err as Error & { code?: string }).code = STAGE_LOCKED_CODE;
    return err;
};

/** ¿Este error es un bloqueo de etapa (validación) y no un fallo real? */
export const isStageLockedError = (err: unknown): boolean =>
    typeof err === 'object' && err !== null
    && (err as { code?: string }).code === STAGE_LOCKED_CODE;

/**
 * Texto del bloqueo, ya traducido y nombrando el prospecto.
 *
 * Las llamadas a `t()` van con la clave LITERAL para que
 * `i18n/marcadores.test.ts` pueda comprobar que los marcadores casan.
 */
export const describeStageBlock = (t: Traducir, reason: StageBlockReason, nombre: string): string =>
    reason === 'won-is-final'
        ? t('prospectos.etapa.ganadoEsFinal', { nombre })
        : t('prospectos.etapa.perdidoNoSaltaAGanado', { nombre });
